/**
 * Helper centralisé pour la résolution des rôles METERIS.
 *
 * Source de vérité unique pour le frontend — remplace les définitions
 * dupliquées de INTERNAL_ROLES éparpillées dans le code.
 *
 * Voir reference_rights_architecture.md dans la mémoire Claude.
 *
 * Règle d'or multi-org : un user dont `roles_by_org[orgCode]` est défini
 * voit son rôle racine remplacé par cet override pour l'org en question.
 * Exemple : Ahmed (role: mcf_commercial, roles_by_org: {METERIS: tech_n2})
 * est tech_n2 sur METERIS, mcf_commercial sur MCF.
 */

import { userHasPermission } from './portalAccess';
import { CONTACT_PROFILES } from './contactProfiles';

export { CONTACT_PROFILES };

// Liste canonique des rôles internes METERIS (alignée avec INTERNAL_ROLES backend
// dans config.py). Tout user avec un de ces rôles est *potentiellement* interne ;
// la vraie internalité dépend AUSSI du flag is_external (faux par défaut).
export const INTERNAL_ROLES = [
  // Internes employés directs
  'tech_n1',
  'tech_n2',
  'admin_n1',
  'admin_n2',
  'superadmin',
  'stagiaire',
  // Internes MCF (Centre de Formation)
  'mcf_admin',
  'mcf_trainer',
  'mcf_commercial',
  'mcf_referent_formateur',
  // Internes commerciaux
  'commercial_n1',
  'commercial_n2',
];

// Rôles techniques (filtre dropdown assignation tickets). Sous-ensemble de
// INTERNAL_ROLES — exclut les commerciaux purs qui ne sont pas affectés aux
// tickets opérationnels.
export const TECH_ASSIGNABLE_ROLES = [
  'tech_n1',
  'tech_n2',
  'admin_n1',
  'admin_n2',
  'superadmin',
  'stagiaire',
];

// Rôle de COMPTE externe (`portal_users.role`, auto-création SSO / espace client).
// Autre vocabulaire que les profils de CONTACT (lib/contactProfiles.js, source unique
// — registre #20266 8f9fdcc1) : cf. reference_rights_architecture.md « Contact ≠ Compte ».
export const EXTERNAL_ACCOUNT_ROLES = ['client'];

// Rôles externes = compte `client` + profils de contact. Ne doivent PAS apparaître
// dans la page Droits.
export const EXTERNAL_ROLES = [...EXTERNAL_ACCOUNT_ROLES, ...CONTACT_PROFILES];

/**
 * Retourne le rôle effectif d'un user sur une org donnée.
 * - Si l'user a un override `roles_by_org[orgCode]`, c'est lui qui prime.
 * - Sinon, le rôle racine est retourné.
 *
 * @param {object} user - User object (avec .role et .roles_by_org)
 * @param {string} [orgCode] - Code de l'organisation active (ex: 'METERIS', 'MCF')
 * @returns {string|null}
 */
export function getEffectiveRole(user, orgCode) {
  if (!user) return null;
  if (orgCode && user.roles_by_org && user.roles_by_org[orgCode]) {
    return user.roles_by_org[orgCode];
  }
  return user.role || null;
}

/**
 * Détermine si un user est INTERNE METERIS sur l'org active.
 * - Tient compte de roles_by_org (un mcf_commercial racine avec
 *   roles_by_org.METERIS=tech_n2 est interne sur METERIS).
 * - Tient compte du flag is_external (un tech_n1 marqué externe = externe).
 *
 * @param {object} user
 * @param {string} [orgCode]
 * @returns {boolean}
 */
export function isUserInternalForOrg(user, orgCode) {
  if (!user) return false;
  if (user.is_external) return false;
  const r = getEffectiveRole(user, orgCode);
  return INTERNAL_ROLES.includes(r);
}

/**
 * Rapports « internes » (rapport technique, envois par mail) : rôle effectif
 * interne, is_external NEUTRALISÉ — miroir de la garde backend de
 * ticket_report_pdf / send-email (registre #20266, fiches rapclitec et 5e5db500).
 * Un prestataire garde l'accès (statu quo) ; client et compta non.
 */
export function canUseInternalReports(user, orgCode) {
  if (!user) return false;
  if (user.role === 'superadmin') return true;
  return INTERNAL_ROLES.includes(getEffectiveRole(user, orgCode));
}

/**
 * Détermine si un user est ASSIGNABLE sur un ticket (techs purs, exclut
 * commerciaux/MCF qui n'ont pas vocation à être assignés à des tickets ops).
 *
 * @param {object} user
 * @param {string} [orgCode]
 * @returns {boolean}
 */
export function isUserAssignableForOrg(user, orgCode) {
  if (!user) return false;
  if (user.is_external) return false;
  if (!user.active) return false;
  const r = getEffectiveRole(user, orgCode);
  return TECH_ASSIGNABLE_ROLES.includes(r);
}

/**
 * Résout une valeur d'organisation (l'`_id` minuscule stocké dans
 * `organization_id`, OU le `code` canonique majuscule, casse quelconque) vers
 * le CODE canonique servant à indexer `roles_by_org` / `permissions_by_org`.
 *
 * Indispensable pour le filtrage org-aware d'assignation : un ticket stocke son
 * org en `organization_id` = `_id` minuscule ("meteris"), alors que
 * `roles_by_org` est indexé par code canonique ("METERIS"). Sans cette
 * résolution, un technicien dont le rôle technique vient UNIQUEMENT de
 * `roles_by_org` (ex : Ahmed, racine `mcf_commercial` + `roles_by_org.METERIS =
 * tech_n2`) disparaît du dropdown d'assignation. Jumeau frontend du Piège 14
 * (cf reference_rights_architecture.md).
 *
 * @param {string} value - org `_id` ou `code`, casse quelconque
 * @param {Array<{id:string, code:string}>} organizations - liste depuis useOrg()
 * @returns {string|null} code canonique, ou null si introuvable
 */
export function resolveOrgCode(value, organizations) {
  if (!value) return null;
  const list = organizations || [];
  const v = String(value).toLowerCase();
  // Match prioritaire par _id (cas nominal : organization_id = _id minuscule).
  const byId = list.find((o) => String(o.id || '').toLowerCase() === v);
  if (byId) return byId.code || null;
  // Repli : la valeur est déjà un code canonique (ex SCI dont l'_id diffère du code).
  const byCode = list.find((o) => String(o.code || '').toLowerCase() === v);
  return byCode ? byCode.code || null : null;
}

// ── Visibilité items (followups / tasks / attachments) ───────────────────────
//
// 3 niveaux alignés sur le backend (voir core/visibility_policy.py) :
//   - "public"     : visible client (externe)
//   - "internal"   : visible internes uniquement
//   - "restricted" : visible avec permission content.restricted.view
//
// Règle anti-bug "set <= read" : un user ne peut SET que les niveaux qu'il
// peut READ. Évite le bug observé 2026-05-25 où un tech sans
// content.restricted.view pouvait passer par "restricted" dans un toggle
// cyclique et perdre l'item à l'écran.

export const VISIBILITY_LEVELS = ['public', 'internal', 'restricted'];
export const DEFAULT_ATTACHMENT_VISIBILITY = 'internal'; // secure-by-default

/**
 * Retourne le niveau effectif de visibilité d'un item, avec fallback rétrocompat
 * sur le champ legacy `is_private`.
 *
 * @param {object} item
 * @returns {'public' | 'internal' | 'restricted'}
 */
export function deriveEffectiveVisibility(item) {
  if (!item) return 'public';
  if (VISIBILITY_LEVELS.includes(item.visibility)) return item.visibility;
  return item.is_private ? 'internal' : 'public';
}

/**
 * Retourne la liste des niveaux de visibilité que cet user a le droit de SET.
 * Par construction, set <= read : on n'autorise jamais à mettre un item dans
 * un niveau qu'on ne pourrait pas voir ensuite.
 *
 * Mirror exact de `get_allowed_visibility_levels` dans le backend.
 *
 * @param {object} user
 * @param {string} [orgCode] - Code de l'org active
 * @returns {string[]} - Tableau (pas Set) pour faciliter usage React .map()
 */
export function getAllowedVisibilityLevels(user, orgCode) {
  if (!user) return [];
  const role = user.role || '';
  if (role === 'superadmin') return ['public', 'internal', 'restricted'];
  if (role === 'client') return [];
  const base = ['public', 'internal'];
  if (userHasPermission(user, 'content.restricted.view', orgCode)) {
    base.push('restricted');
  }
  return base;
}

/**
 * Vrai si l'user peut LIRE un item de cette visibilité.
 * Utile pour décider si on affiche un menu contextuel sur cet item.
 *
 * @param {object} user
 * @param {string} visibility
 * @param {string} [orgCode]
 * @returns {boolean}
 */
export function canActorReadVisibility(user, visibility, orgCode) {
  if (!user) return visibility === 'public';
  const role = user.role || '';
  if (role === 'superadmin') return true;
  if (role === 'client' || user.is_external) return visibility === 'public';
  if (visibility === 'restricted') {
    return userHasPermission(user, 'content.restricted.view', orgCode);
  }
  return true;
}

// ── Visibilité hiérarchique ticket (Phase 4 — refonte 2026-06) ──────────────
//
// Miroir du backend ROLE_RANK dans core/ticket_visibility.py.
// Utilisé côté front UNIQUEMENT pour savoir si l'acteur peut basculer le flag
// Confidentiel (délégué à canActorChangeTicketConfidential) ; le calcul réel
// de qui voit quoi reste 100 % côté backend (single source of truth).

/** Rang numérique par rôle — aligné sur backend ROLE_RANK. */
export const ROLE_RANK = { stagiaire: 1, tech_n1: 1, tech_n2: 2, admin_n1: 3, admin_n2: 4 };

/** Rang fictif des superadmins — jamais exclus par la règle de rang. */
export const SUPERADMIN_RANK = 1000000;

/**
 * Retourne le rang hiérarchique de visibilité d'un acteur.
 * 0 = participation directe seule (externe, client, rôle inconnu).
 *
 * @param {object} user
 * @param {string} [orgCode]
 * @returns {number}
 */
export function getRoleRank(user, orgCode) {
  if (!user) return 0;
  const role = getEffectiveRole(user, orgCode);
  if (role === 'superadmin') return SUPERADMIN_RANK;
  if (user.is_external) return 0;
  return ROLE_RANK[role] ?? 0;
}

/**
 * Vrai si l'acteur courant peut basculer le flag Confidentiel du ticket.
 * Le backend est l'autorité réelle ; on s'aligne sur le flag `can_edit`
 * déjà renvoyé par l'API pour éviter toute redondance de logique.
 *
 * @param {boolean} canEditTicket - Valeur de `ticket.can_edit !== false`
 * @returns {boolean}
 */
export function canActorChangeTicketConfidential(canEditTicket) {
  return !!canEditTicket;
}

/**
 * Retourne un libellé lisible pour un rang de visibilité numérique.
 *
 * @param {number|null} rank
 * @returns {string}
 */
export function getVisibilityRankLabel(rank) {
  const m = { 1: 'Technicien N1', 2: 'Technicien N2', 3: 'Admin N1', 4: 'Admin N2' };
  return m[rank] || (rank != null ? `Niveau ${rank}` : '');
}

/**
 * Vrai si l'user peut RECLASSER une PJ déjà attachée.
 * Règle : (uploader de la PJ) OU (peut éditer le ticket).
 *
 * Le check `canEditTicket` n'est pas dans ce fichier — appelant doit le
 * pré-évaluer et passer `canEdit: boolean` en argument pour éviter une
 * dépendance circulaire.
 *
 * @param {object} user
 * @param {object} doc - Document/PJ (avec .uploaded_by ou .uploader_email)
 * @param {boolean} canEdit - Résultat de canEditTicket(actor, ticket)
 * @returns {boolean}
 */
export function canChangeAttachmentVisibility(user, doc, canEdit) {
  if (!user || !doc) return false;
  if (user.role === 'client' || user.is_external) return false;
  const myEmail = (user.email || '').toLowerCase();
  const uploader = String(doc.uploaded_by || doc.uploader_email || '').toLowerCase();
  if (myEmail && uploader && myEmail === uploader) return true;
  return Boolean(canEdit);
}

/**
 * Retire les comptes de test E2E (is_e2e_test) d'une liste d'utilisateurs,
 * SAUF ceux dont l'id ou l'email figure dans `keep` (valeur déjà sélectionnée).
 *
 * Usage : masquer les techniciens « crawler » des menus déroulants de sélection
 * tout en gardant affiché un crawler déjà assigné. Filtre purement cosmétique —
 * aucun impact droits (cf reference_rights_architecture.md). Le matching id ET
 * email gère le piège « assigned_user_id = email OU UUID »
 * (cf reference_ticketing_identities.md).
 *
 * @param {Array<object>} users
 * @param {object} [opts]
 * @param {string|string[]} [opts.keep] - id(s)/email(s) à conserver même si test
 * @returns {Array<object>}
 */
export function excludeTestUsers(users, { keep } = {}) {
  if (!Array.isArray(users)) return [];
  const keepSet = new Set(
    (Array.isArray(keep) ? keep : [keep])
      .filter(Boolean)
      .map(v => String(v).toLowerCase())
  );
  return users.filter(u => {
    if (!u || !u.is_e2e_test) return true;
    const id = u.id != null ? String(u.id).toLowerCase() : '';
    const email = u.email != null ? String(u.email).toLowerCase() : '';
    return keepSet.has(id) || keepSet.has(email);
  });
}
